import { ref, computed, watchEffect } from 'vue'
import { defineStore } from 'pinia'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabaseClient'
import { createLogger } from '@/lib/logger'

const log = createLogger('Auth')

type AalLevel = 'aal1' | 'aal2' | 'unknown'

const adminEmails = new Set(
  String((import.meta.env as Record<string, unknown>).VITE_ADMIN_EMAILS || '')
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean),
)

let listenerRegistered = false

export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null)
  const aal = ref<AalLevel>('unknown')
  const totpVerified = ref(false)
  const ready = ref(false)
  const loading = ref(false)
  const lastFactorCheck = ref<number | null>(null)
  const adminTableMatch = ref(false)
  const adminTableChecked = ref(false)

  const user = computed<User | null>(() => session.value?.user ?? null)

  const isAdmin = computed(() => {
    const currentUser = user.value
    if (!currentUser) return false
    const role = (currentUser.app_metadata as Record<string, unknown>)?.role
    if (role === 'admin') return true
    if (adminTableMatch.value) return true
    const email = (currentUser.email || '').toLowerCase()
    return email ? adminEmails.has(email) : false
  })

  const initials = computed(() => {
    const currentUser = user.value
    if (!currentUser) return '??'
    const first = String(currentUser.user_metadata?.first_name || '').trim()
    const last = String(currentUser.user_metadata?.last_name || '').trim()
    const fallback = String(currentUser.email || '').split('@')[0] || ''
    const name = [first, last].filter(Boolean).join(' ') || fallback
    return (
      name
        .split(/\s+/)
        .map((part) => part.trim()[0])
        .filter(Boolean)
        .join('')
        .slice(0, 2)
        .toUpperCase() || 'US'
    )
  })

  const displayName = computed(() => {
    const currentUser = user.value
    if (!currentUser) return 'Guest'
    const first = String(currentUser.user_metadata?.first_name || '').trim()
    const last = String(currentUser.user_metadata?.last_name || '').trim()
    if (first || last) return [first, last].filter(Boolean).join(' ')
    return String(currentUser.email || '').split('@')[0] || 'User'
  })

  async function evaluateAal(nextSession: Session | null) {
    if (!nextSession) {
      aal.value = 'unknown'
      return
    }

    try {
      // Check the actual AAL level from the session first
      const sessionAal = (nextSession as any)?.aal || 'aal1'
      log.debug('Session AAL from Supabase:', sessionAal)

      // Also check via MFA method with timeout
      let mfaAal = 'aal1'
      try {
        const aalRes = await Promise.race([
          supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('AAL timeout')), 2000)),
        ])
        mfaAal = (aalRes as any)?.data?.currentLevel || 'aal1'
        log.debug('AAL from MFA method:', mfaAal)
      } catch (error) {
        log.warn('MFA AAL check failed:', error)
      }

      // Use the actual AAL from session or MFA method - don't just assume AAL2
      aal.value = sessionAal === 'aal2' || mfaAal === 'aal2' ? 'aal2' : 'aal1'
      log.debug('Final AAL decision:', aal.value)
    } catch (error) {
      log.warn('AAL evaluation failed, defaulting to aal1:', error)
      aal.value = 'aal1'
    }
  }

  async function refreshTotpStatus(force = false) {
    if (!session.value) {
      totpVerified.value = false
      return false
    }

    const now = Date.now()
    if (!force && lastFactorCheck.value && now - lastFactorCheck.value < 30000) {
      return totpVerified.value
    }

    try {
      log.debug('Checking TOTP status for user:', session.value.user?.email)

      // Add timeout to prevent hangs
      const factorsPromise = supabase.auth.mfa.listFactors()
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('TOTP check timeout')), 2000),
      )

      const factors = (await Promise.race([factorsPromise, timeoutPromise])) as any
      const verifiedTotp = (factors.data?.totp || []).find((f: any) => f.status === 'verified')
      totpVerified.value = Boolean(verifiedTotp)
      lastFactorCheck.value = now

      log.debug('TOTP status check result:', {
        hasVerifiedTotp: totpVerified.value,
        factorCount: factors.data?.totp?.length || 0,
        verifiedFactor: verifiedTotp ? { id: verifiedTotp.id, status: verifiedTotp.status } : null,
      })

      return totpVerified.value
    } catch (error) {
      log.warn('Failed to refresh TOTP status:', error)
      // If TOTP check fails, assume user has TOTP (to prevent lockout)
      totpVerified.value = true
      lastFactorCheck.value = now
      return true
    }
  }

  async function refreshAdminFlag(force = false) {
    const userId = session.value?.user?.id
    if (!userId) {
      adminTableMatch.value = false
      adminTableChecked.value = true
      return false
    }
    if (!force && adminTableChecked.value) return adminTableMatch.value
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id')
        .eq('id', userId)
        .maybeSingle()
      if (error) throw error
      adminTableMatch.value = Boolean(data?.id)
    } catch {
      adminTableMatch.value = false
    } finally {
      adminTableChecked.value = true
    }
    return adminTableMatch.value
  }

  async function applySession(nextSession: Session | null) {
    session.value = nextSession
    await evaluateAal(nextSession)
    await refreshTotpStatus(true)
    await refreshAdminFlag(true)
  }

  async function refreshSession() {
    loading.value = true
    try {
      const { data } = await supabase.auth.getSession()
      await applySession(data.session ?? null)
    } finally {
      loading.value = false
      ready.value = true
    }
    return session.value
  }

  async function bootstrap() {
    if (ready.value && session.value) {
      await refreshAdminFlag(true)
      return session.value
    }
    return refreshSession()
  }

  async function ensureTotp(force = false) {
    return refreshTotpStatus(force)
  }

  async function ensureAdmin(force = false) {
    return refreshAdminFlag(force)
  }

  async function signOut() {
    log.debug('Signing out...')
    try {
      // Add timeout to prevent hangs
      const signOutPromise = supabase.auth.signOut()
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Sign out timeout')), 3000),
      )

      await Promise.race([signOutPromise, timeoutPromise])
      log.debug('Supabase sign out successful')
    } catch (error) {
      log.warn('Sign out error (continuing anyway):', error)
    }

    // Always clear state regardless of Supabase response
    try {
      if (typeof window !== 'undefined') {
        localStorage.clear()
        sessionStorage.clear()
      }
    } catch (error) {
      log.warn('Storage clear error:', error)
    }

    // Reset all state
    session.value = null
    aal.value = 'unknown'
    totpVerified.value = false
    ready.value = false
    loading.value = false
    lastFactorCheck.value = null
    adminTableMatch.value = false
    adminTableChecked.value = false

    log.debug('Sign out complete')
  }

  async function forceSignOut() {
    try {
      await supabase.auth.signOut()
      // Clear localStorage to remove any cached session
      if (typeof window !== 'undefined') {
        localStorage.removeItem('sb-dylpwpmkbxvlhunqwmcd-auth-token')
        localStorage.removeItem('supabase.auth.token')
      }
    } finally {
      session.value = null
      aal.value = 'unknown'
      totpVerified.value = false
      ready.value = true
      lastFactorCheck.value = Date.now()
    }
  }

  async function forceRefresh() {
    log.debug('Force refreshing auth state...')
    loading.value = true
    try {
      // Get fresh session
      const { data } = await supabase.auth.getSession()
      log.debug('Fresh session data:', data.session?.user?.email, 'AAL:', (data.session as any)?.aal)

      await applySession(data.session ?? null)

      // Force refresh admin status
      await refreshAdminFlag(true)

      log.debug('Force refresh complete. AAL:', aal.value, 'Admin:', isAdmin.value)
    } finally {
      loading.value = false
      ready.value = true
    }
    return { session: session.value, aal: aal.value, isAdmin: isAdmin.value }
  }

  if (!listenerRegistered) {
    try {
      supabase.auth.onAuthStateChange(async (_event, nextSession) => {
        await applySession(nextSession)
        ready.value = true
      })
      listenerRegistered = true
    } catch (e) {
      log.warn('Failed to register auth state listener:', e)
    }
  }

  watchEffect(() => {
    if (!session.value) return
    void refreshTotpStatus(false)
  })

  return {
    session,
    user,
    aal,
    totpVerified,
    ready,
    loading,
    displayName,
    initials,
    isAdmin,
    bootstrap,
    refreshSession,
    ensureTotp,
    signOut,
    forceSignOut,
    forceRefresh,
    ensureAdmin,
  }
})
